// src/services/deckService.js
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  addDoc,
  doc,
  updateDoc,
  deleteDoc,
  getDoc,
  writeBatch, // <--- Import writeBatch for efficient deletion
} from "firebase/firestore";
import { db } from "../auth/auth";
import { createSlide, getSlidesByDeck, deleteSlide } from "./slideService"; // <--- Import getSlidesByDeck and deleteSlide

const decksRef = collection(db, "decks");

export async function getUserDecks(userId) {
  const q = query(
    decksRef,
    where("ownerId", "==", userId),
    orderBy("updatedAt", "desc")
  );

  const snapshot = await getDocs(q);

  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));
}

/**
 * Create a new deck and its initial slide
 */
export async function createDeck(userId) {
  const now = Date.now();

  const newDeckData = {
    ownerId: userId,
    title: "Untitled Deck",
    createdAt: now,
    updatedAt: now,
    slides: [],
    settings: {
      theme: "light",
      aspectRatio: "9:16",
    },
    meta: {
      slideCount: 0,
    },
  };

  const docRef = await addDoc(decksRef, newDeckData);
  const newDeckId = docRef.id;

  // Create the initial slide for this new deck
  const firstSlide = await createSlide({
    deckId: newDeckId,
    ownerId: userId,
    order: 1000,
  });

  // Update the deck with the first slide's ID and count for consistency
  await updateDoc(docRef, {
    slides: [firstSlide.id],
    meta: {
      slideCount: 1,
    },
    updatedAt: Date.now(),
  });

  return {
    id: newDeckId,
    ...newDeckData,
    slides: [firstSlide.id],
    meta: {
      slideCount: 1,
    },
  };
}

/**
 * Rename a deck
 */
export async function renameDeck(deckId, title) {
  const deckRef = doc(db, "decks", deckId);

  await updateDoc(deckRef, {
    title,
    updatedAt: Date.now(),
  });
}

/**
 * Delete a deck and all its associated slides.
 * This function now requires the ownerId for fetching slides.
 */
export async function deleteDeck(deckId, ownerId) { // <--- Add ownerId parameter
  if (!deckId || !ownerId) {
    throw new Error("deckId or ownerId missing for deleteDeck");
  }

  // 1. Get all slides for the deck
  const slidesToDelete = await getSlidesByDeck(deckId, ownerId);

  // 2. Create a batch to delete all slides and the deck efficiently
  const batch = writeBatch(db);

  // Add all slide deletions to the batch
  slidesToDelete.forEach(slide => {
    const slideRef = doc(db, "slides", slide.id);
    batch.delete(slideRef);
  });

  // Add the deck deletion to the batch
  const deckRef = doc(db, "decks", deckId);
  batch.delete(deckRef);

  // Commit the batch
  await batch.commit();
}

/**
 * Get single deck (Editor page)
 */
export async function getDeckById(deckId) {
  const deckRef = doc(db, "decks", deckId);
  const snap = await getDoc(deckRef);

  if (!snap.exists()) return null;

  return {
    id: snap.id,
    ...snap.data(),
  };
}
